import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { Spinner } from '@trello/nachos/spinner';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { usePluginOrganizationOwnerQuery } from '../queries/PluginOrganizationOwnerQuery.generated';
import { MemberList } from './MemberList';

import styles from './PowerUpCollaboratorsPage.less';

export const PowerUpCollaboratorsPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, error, loading } = usePluginOrganizationOwnerQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  if (error) {
    return (
      <div>
        <FormattedMessage
          id="templates.app_management.something-went-wrong"
          defaultMessage="Something went wrong. Please try again later."
          description="Error message shown when something goes wrong"
        />
      </div>
    );
  }

  const plugin = data?.plugin;

  if (loading || !plugin) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner
          testId={getTestId<AppManagementTestIds>(
            'power-up-collaborators-page-spinner',
          )}
        />
      </div>
    );
  }

  const { idOrganizationOwner } = plugin;

  Analytics.sendScreenEvent({
    name: 'powerUpAdminCollaboratorsScreen',
    containers: {
      organization: { id: idOrganizationOwner },
    },
    attributes: { idPlugin },
  });

  return (
    <>
      <div className={styles.header}>
        <h1>
          <FormattedMessage
            id="templates.app_management.collaborators"
            defaultMessage="Collaborators"
            description="Title for the collaborators section"
          />
        </h1>
      </div>
      <div>
        <p>
          <FormattedMessage
            id="templates.app_management.collaborators-description"
            defaultMessage="Collaborators are members of your Workspace who have permission to manage this Power-Up. Any collaborator or admin of this Workspace can add or remove other collaborators."
            description="Description of what collaborators are"
          />
        </p>
        <p>
          <FormattedMessage
            id="templates.app_management.collaborators-contact"
            defaultMessage="If we need to contact you about this Power-Up, we'll create a Jira issue with all the collaborators added to it."
            description="Information about how collaborators will be contacted"
          />
          <a
            target="_blank"
            className={styles.learnMoreSubtle}
            rel="noopener noreferrer"
            href="https://community.developer.atlassian.com/t/power-up-developers-changes-in-the-way-we-communicate-with-you/79361"
          >
            {' '}
            <FormattedMessage
              id="templates.app_management.collaborators-learn-more"
              defaultMessage="Learn more about this change here"
              description="Link text to learn more about collaborator changes"
            />
          </a>
        </p>
      </div>
      <MemberList
        idPlugin={idPlugin}
        idOrganizationOwner={idOrganizationOwner}
      />
    </>
  );
};
